import { Box, Button, Chip, Typography, alpha, useTheme } from "@mui/material";
import { Close, FilterList } from "@mui/icons-material";
import { AppliedFilter } from "src/utils/filterUrlUtils";

interface AppliedFiltersBarProps {
  /** Currently applied filters */
  filters: AppliedFilter[];

  /** Callback to remove a single filter */
  onRemove: (filterId: string) => void;

  /** Callback to clear all filters */
  onClearAll: () => void;

  /** Whether to show the filter icon */
  showIcon?: boolean;

  /** Whether to show the "Clear all" button */
  showClearAll?: boolean;

  /** Maximum number of chips to show before collapsing */
  maxVisible?: number;

  /** Label shown before the chips */
  label?: string;
}

/**
 * Displays applied filters as removable chips
 *
 * Features:
 * - Compact chip display
 * - Individual remove buttons
 * - "Clear all" option
 * - Collapsible for many filters
 * - Themed styling
 */
const AppliedFiltersBar: React.FC<AppliedFiltersBarProps> = ({
  filters,
  onRemove,
  onClearAll,
  showIcon = true,
  showClearAll = true,
  maxVisible = 10,
  label = "Filters",
}) => {
  const theme = useTheme();

  if (filters.length === 0) return null;

  const visibleFilters = filters.slice(0, maxVisible);
  const hiddenCount = filters.length - maxVisible;

  // Group consecutive filters for the same field
  const groupedFilters = groupFiltersByField(visibleFilters);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        py: 1,
        px: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Label with icon */}
      {showIcon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mr: 0.5,
          }}
        >
          <FilterList
            sx={{
              fontSize: "1rem",
              color: theme.palette.text.secondary,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8125rem",
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            {label}:
          </Typography>
        </Box>
      )}

      {/* Filter chips */}
      {groupedFilters.map((group) => (
        <Chip
          key={group.ids.join(",")}
          label={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: theme.palette.primary.dark,
                }}
              >
                {group.label}:
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  color: theme.palette.text.primary,
                  maxWidth: 150,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {group.displayValue}
              </Typography>
            </Box>
          }
          onDelete={() => {
            // Remove all filters in this group
            group.ids.forEach((id) => onRemove(id));
          }}
          deleteIcon={
            <Close
              sx={{
                fontSize: "0.875rem !important",
                color: `${theme.palette.text.secondary} !important`,
                "&:hover": {
                  color: `${theme.palette.error.main} !important`,
                },
              }}
            />
          }
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: "4px",
            height: 26,
            "& .MuiChip-label": {
              px: 1,
            },
            "& .MuiChip-deleteIcon": {
              mr: 0.5,
            },
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
            },
          }}
        />
      ))}

      {/* Hidden count indicator */}
      {hiddenCount > 0 && (
        <Chip
          label={`+${hiddenCount} more`}
          size="small"
          variant="outlined"
          sx={{
            borderRadius: "4px",
            height: 26,
            fontSize: "0.75rem",
            borderColor: theme.palette.divider,
          }}
        />
      )}

      {/* Clear all button */}
      {showClearAll && filters.length > 1 && (
        <Button
          size="small"
          onClick={onClearAll}
          sx={{
            ml: "auto",
            fontSize: "0.75rem",
            color: theme.palette.text.secondary,
            textTransform: "none",
            minWidth: "auto",
            py: 0.25,
            px: 1,
            "&:hover": {
              color: theme.palette.error.main,
              backgroundColor: alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          Clear all
        </Button>
      )}
    </Box>
  );
};

// Helper to group filters by field for cleaner display
interface FilterGroup {
  fieldName: string;
  label: string;
  displayValue: string;
  ids: string[];
}

function groupFiltersByField(filters: AppliedFilter[]): FilterGroup[] {
  const groups = new Map<string, FilterGroup>();

  filters.forEach((filter) => {
    // For range filters, group min/max together
    const key = filter.fieldName;

    if (groups.has(key)) {
      const group = groups.get(key)!;
      group.ids.push(filter.id);

      // Combine display values
      if (filter.operator === "ge" || filter.operator === "gt") {
        const existingMax = group.displayValue;
        group.displayValue = `${filter.displayValue} – ${existingMax}`;
      } else if (filter.operator === "le" || filter.operator === "lt") {
        const existingMin = group.displayValue;
        if (existingMin.includes("–")) {
          // Already has a range, update the max
          group.displayValue = existingMin.replace(
            /–.*$/,
            `– ${filter.displayValue.replace(/[≤<]\s*/, "")}`,
          );
        } else {
          group.displayValue = `${existingMin} – ${filter.displayValue.replace(
            /[≤<]\s*/,
            "",
          )}`;
        }
      } else {
        // Multi-value equality
        group.displayValue = `${group.displayValue}, ${filter.displayValue}`;
      }
    } else {
      groups.set(key, {
        fieldName: filter.fieldName,
        label: filter.displayLabel,
        displayValue: filter.displayValue,
        ids: [filter.id],
      });
    }
  });

  return Array.from(groups.values());
}

export default AppliedFiltersBar;
